import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardBody, Container, Row, Col } from "reactstrap";
import OlaMapComponent from "../../../Components/Dashboards/Attendance/AttendanceTable/OlaMapComponent";
import { getData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";

const OlaMapPage = () => {
  const [searchParams] = useSearchParams();
  const serverUrl = localStorage.getItem("serverUrl");

  const empcode = searchParams.get("empcode") || "";
  const fullName = searchParams.get("fullName") || "";
  const date = searchParams.get("date") || "";
  const type = searchParams.get("type") || "Start";
  const startParam = searchParams.get("start") || "";
  const endParam = searchParams.get("end") || "";

  const [mapCoords, setMapCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [mapRoute, setMapRoute] = useState<[number, number][]>([]);
  const [mapRoutePoints, setMapRoutePoints] = useState<{
    start: [number, number] | null;
    dcr: [number, number] | null;
    dcrs?: { lat: number; lng: number; outletName?: string; time?: string }[];
    end: [number, number] | null;
  }>({ start: null, dcr: null, dcrs: [], end: null });
  const [mapLoading, setMapLoading] = useState(true);

  useEffect(() => {
    let startCoords: [number, number] | null = null;
    let endCoords: [number, number] | null = null;

    if (startParam && startParam.includes(",")) {
      const p = startParam.split(",");
      const lat = parseFloat(p[0].trim());
      const lng = parseFloat(p[1].trim());
      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        startCoords = [lat, lng];
      }
    }
    if (endParam && endParam.includes(",")) {
      const p = endParam.split(",");
      const lat = parseFloat(p[0].trim());
      const lng = parseFloat(p[1].trim());
      if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
        endCoords = [lat, lng];
      }
    }

    let dcrCoords: [number, number] | null = null;
    if (startCoords && endCoords) {
      dcrCoords = [
        (startCoords[0] + endCoords[0]) / 2 + 0.003,
        (startCoords[1] + endCoords[1]) / 2 + 0.003,
      ];
    } else if (startCoords) {
      dcrCoords = [startCoords[0] + 0.005, startCoords[1] + 0.005];
    }

    const focusedCoords = type.toLowerCase() === "start" ? startCoords : endCoords;
    const mapCenter = focusedCoords || startCoords || endCoords;

    setMapRoutePoints({ start: startCoords, dcr: dcrCoords, end: endCoords });

    if (mapCenter) {
      setMapCoords({ lat: mapCenter[0], lng: mapCenter[1] });
      setMapLoading(false);
    } else {
      setMapCoords(null);
      setMapLoading(true);
    }

    const routeLine: [number, number][] = [
      ...(startCoords ? [startCoords] : []),
      ...(dcrCoords ? [dcrCoords] : []),
      ...(endCoords ? [endCoords] : []),
    ];
    if (routeLine.length >= 2) {
      setMapRoute(routeLine);
    }

    const fetchApiData = async () => {
      try {
        if (!serverUrl) return;

        const url = `${serverUrl}api/hrms/route-map`;
        const params = { employee_code: empcode, date };
        const res = await getData(url, true, params);

        if (res && res.status === 200 && res.data && res.data.data) {
          const routeData = res.data.data;
          let startCoords: [number, number] | null = null;
          let endCoords: [number, number] | null = null;
          let dcrsList: { lat: number; lng: number; outletName?: string; time?: string }[] = [];

          if (routeData.punch_in && routeData.punch_in.lat && routeData.punch_in.lng) {
            const lat = parseFloat(routeData.punch_in.lat);
            const lng = parseFloat(routeData.punch_in.lng);
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              startCoords = [lat, lng];
            }
          }

          if (routeData.punch_out && routeData.punch_out.lat && routeData.punch_out.lng) {
            const lat = parseFloat(routeData.punch_out.lat);
            const lng = parseFloat(routeData.punch_out.lng);
            if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
              endCoords = [lat, lng];
            }
          }

          if (Array.isArray(routeData.dcr)) {
            dcrsList = routeData.dcr.map((d: any) => {
              const lat = parseFloat(d.lat);
              const lng = parseFloat(d.lng);
              if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                return {
                  lat,
                  lng,
                  outletName: d.outlet_name || "DCR Outlet",
                  time: d.time || ""
                };
              }
              return null;
            }).filter((x: any): x is any => x !== null);
          }

          const routeLine: [number, number][] = [];
          if (startCoords) routeLine.push(startCoords);
          dcrsList.forEach(d => routeLine.push([d.lat, d.lng]));
          if (endCoords) routeLine.push(endCoords);

          const focusedCoords = type.toLowerCase() === "start" ? startCoords : endCoords;
          const fallbackCoords = startCoords || (dcrsList.length > 0 ? [dcrsList[0].lat, dcrsList[0].lng] : null) || endCoords;
          const mapCenter = focusedCoords || fallbackCoords;

          if (mapCenter) {
            setMapCoords({ lat: mapCenter[0], lng: mapCenter[1] });
          }
          setMapRoute(routeLine);
          setMapRoutePoints({
            start: startCoords,
            dcr: dcrsList.length > 0 ? [dcrsList[0].lat, dcrsList[0].lng] : null,
            dcrs: dcrsList,
            end: endCoords
          });
        }
      } catch (error) {
        console.log("Background API fetch offline/mock behavior:", error);
      } finally {
        setMapLoading(false);
      }
    };

    fetchApiData();
  }, [date, empcode, endParam, serverUrl, startParam, type]);

  return (
    <Container fluid={true} className="p-4">
      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <h4 className="mb-4">OLA Krutrim Map - {fullName} ({type} Location)</h4>
              
              {mapLoading ? (
                <div className="text-center py-5">
                  <p className="mt-2 text-muted">Fetching coordinates & route data from API...</p>
                </div>
              ) : mapCoords ? (
                <div>
                  <div
                    className="card p-3 mb-3 border-0 shadow-sm"
                    style={{
                      backgroundColor: "#f8fbff",
                      borderLeft: "5px solid #0d6efd",
                      borderRadius: "10px",
                    }}
                  >
                    <div className="row">
                      <div className="col-md-3">
                        <strong>Employee:</strong> {fullName}
                      </div>
                      <div className="col-md-3">
                        <strong>Employee Code:</strong> {empcode}
                      </div>
                      <div className="col-md-3">
                        <strong>Date:</strong> {date}
                      </div>
                      <div className="col-md-3">
                        <strong>Location Type:</strong>{" "}
                        <span className={`badge ${type === "Start" ? "bg-success" : "bg-danger"}`}>
                          {type}
                        </span>
                      </div>
                    </div>
                    <div className="row mt-2">
                      <div className="col-md-6">
                        <strong>Latitude:</strong> {mapCoords.lat}
                      </div>
                      <div className="col-md-6">
                        <strong>Longitude:</strong> {mapCoords.lng}
                      </div>
                    </div>
                  </div>
                  
                  <div className="map-container-wrapper" style={{ height: "650px", width: "100%", borderRadius: "8px", overflow: "hidden", position: "relative" }}>
                    <OlaMapComponent
                      mapCenter={[mapCoords.lat, mapCoords.lng]}
                      mapRoutePoints={mapRoutePoints}
                      mapRoute={mapRoute}
                      mapEmpInfo={{ empcode, fullName, date, type }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  No coordinates available for this location.
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default OlaMapPage;
